import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import { IFunctionalLocation } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { handleNameTranslation } from "utilities/localesUtilities";
import useRolesPermissions from "hooks/useRolesPermissions";
import { useStyles } from "../AreasList.styles";
import { FunctionalLocationItem } from "./FunctionalLocationItem";
import { ActionButtons } from "./ActionButtons";
import { SortableFunctionalLocationCard } from "./SortableFunctionalLocationCard";
import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useAreasListContext } from "../context/AreasListContextProvider";
import { useFunctionalLocationsReorder } from "../hooks/useFunctionalLocationsReorder";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { IAreaItemProps } from "./interfaces";

export const AreaItem: React.FC<IAreaItemProps> = ({ area, isDragging }) => {
    const {
        areaTabs,
        areas,
        refetch,
        setAreas,
        toggleAreaTab,
        setAreaToEdit,
        setAreaToDelete,
        handleOpenFunctionalLocationModal,
    } = useAreasListContext();
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

    const { setFunctionalLocationsOrder } = FailureDetailsEntity.SetFunctionalLocationsOrderByArea();

    const {
        isDraggingFunctionalLocations,
        onDragMove: onFunctionalLocationDragMove,
        onDragEnd: onFunctionalLocationDragEnd,
    } = useFunctionalLocationsReorder({ areas, setAreas, refetch, setFunctionalLocationsOrder });

    const upCaretStyles = {
        transform: "rotate(180deg)",
    };

    const downCaretStyles = {
        transform: "rotate(270deg)",
    };

    const functionalLocations = useMemo(() => area.functional_locations, [area.functional_locations]);

    const renderFunctionalLocation = (functionalLocation: IFunctionalLocation) => {
        const functionalLocationContent = (
            <FunctionalLocationItem
                key={functionalLocation.functional_location_id}
                functionalLocation={functionalLocation}
                areaId={area.area_id}
                isDraggingFunctionalLocations={isDraggingFunctionalLocations}
            />
        );

        if (CORPORATE_PERMISSIONS?.corporationPermissions?.canDragAndDropMachineCenters) {
            return (
                <SortableFunctionalLocationCard
                    key={functionalLocation.functional_location_id}
                    id={functionalLocation.functional_location_id.toString()}
                >
                    {functionalLocationContent}
                </SortableFunctionalLocationCard>
            );
        }

        return functionalLocationContent;
    };

    return (
        <Box>
            <ListItemButton
                onMouseUp={() => {
                    toggleAreaTab(area.area_id, isDragging);
                }}
                className={classes.areaListItemButton}
            >
                {areaTabs.includes(area.area_id) ? (
                    <ExpandLess style={upCaretStyles} className={classes.caret} />
                ) : (
                    <ExpandMore style={downCaretStyles} className={classes.caret} />
                )}
                <ListItemText
                    primary={handleNameTranslation(i18n.language, area.translation.translation_names)}
                    classes={{ primary: classes.optionsTitle }}
                />
                {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters && (
                    <ActionButtons onEdit={() => setAreaToEdit(area)} onDelete={() => setAreaToDelete(area)} />
                )}
            </ListItemButton>
            <Collapse in={areaTabs.includes(area.area_id)} timeout="auto" unmountOnExit>
                <Box className={classes.listContainer}>
                    <List component="div" disablePadding>
                        <ListItemButton className={classes.addFunctionalLocationItemButton}>
                            {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters && (
                                <Box className={classes.titleContainer}>
                                    <button
                                        className={`${classes.button} ${classes.titleContainer}`}
                                        onMouseUp={() => handleOpenFunctionalLocationModal(area.area_id, null)}
                                    >
                                        <AddCircleOutlinedIcon />
                                        <Typography className={classes.title} variant="subtitle2">
                                            {t("failure_details_setup.add_new_functional_location").toUpperCase()}
                                        </Typography>
                                    </button>
                                </Box>
                            )}

                            {area.functional_locations &&
                                area.functional_locations.length > 0 &&
                                (CORPORATE_PERMISSIONS?.corporationPermissions?.canDragAndDropMachineCenters ? (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragMove={onFunctionalLocationDragMove}
                                        onDragEnd={onFunctionalLocationDragEnd}
                                    >
                                        <SortableContext
                                            items={functionalLocations.map((fl) =>
                                                fl.functional_location_id.toString(),
                                            )}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <List>{functionalLocations.map(renderFunctionalLocation)}</List>
                                        </SortableContext>
                                    </DndContext>
                                ) : (
                                    <List>{functionalLocations.map(renderFunctionalLocation)}</List>
                                ))}
                        </ListItemButton>
                    </List>
                </Box>
            </Collapse>
        </Box>
    );
};
